import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Loader2,
  Camera,
  Image as ImageIcon,
  Sparkles,
  LayoutTemplate,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function PhotoshootGallery() {
  const navigate = useNavigate();
  const [shoots, setShoots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/photoshoot/history`, { withCredentials: true });
      setShoots(res.data.photoshoots || []);
    } catch {
      toast.error("Failed to load photoshoot history");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url) => {
    try {
      const a = document.createElement("a");
      a.href = `${API}/api/download-video?url=${encodeURIComponent(url)}`;
      a.download = `photoshoot-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Image downloaded!");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="photoshoot-gallery">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/photoshoot")}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="gallery-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Camera className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold text-foreground" style={{ fontFamily: "'Clash Display', serif" }}>
            My Photoshoots
          </h1>
        </div>
        <Button
          onClick={() => navigate("/photoshoot")}
          className="bg-[#FF5A36] hover:bg-[#e54e2e] text-white rounded-full px-5"
          data-testid="new-photoshoot-btn"
        >
          New Photoshoot
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!loading && shoots.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="gallery-empty">
          <ImageIcon className="w-12 h-12 text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground text-sm mb-4">No photoshoots yet. Create your first one!</p>
          <Button
            onClick={() => navigate("/photoshoot")}
            className="bg-[#FF5A36] hover:bg-[#e54e2e] text-white rounded-full px-6"
          >
            Create Photoshoot
          </Button>
        </div>
      )}

      {/* Gallery grid */}
      {!loading && shoots.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shoots.map((shoot) => (
            <div
              key={shoot.id}
              className="group bg-card rounded-2xl border border-border overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`gallery-card-${shoot.id}`}
            >
              {/* Image */}
              <div className="relative aspect-square bg-muted">
                <img
                  src={shoot.generated_image_url}
                  alt="Photoshoot"
                  className="w-full h-full object-cover"
                />
                {/* Hover overlay: Download + Edit */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-3">
                  <button
                    onClick={() => handleDownload(shoot.generated_image_url)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                    data-testid={`download-${shoot.id}`}
                  >
                    <Download className="w-4 h-4 text-foreground" />
                  </button>
                  <button
                    onClick={() => navigate("/photoshoot", { state: { editImageUrl: shoot.generated_image_url } })}
                    className="opacity-0 group-hover:opacity-100 transition-opacity w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                    data-testid={`edit-${shoot.id}`}
                  >
                    <Pencil className="w-4 h-4 text-foreground" />
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  {shoot.type === "template" ? (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      <LayoutTemplate className="w-3 h-3" />
                      Template
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-600">
                      <Sparkles className="w-3 h-3" />
                      Prompt
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto">{formatDate(shoot.created_at)}</span>
                </div>
                <p className="text-sm text-foreground truncate">
                  {shoot.type === "template"
                    ? shoot.template_name || "Template photoshoot"
                    : shoot.prompt || "Generated image"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
